import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { NAV, BRAND } from "@/data/content";
import { CTAButton } from "./ui";

function Logo({ home }) {
  return (
    <a href={home ? "#top" : "/"} data-testid="header-logo" className="flex flex-col leading-none">
      <span className="font-serif text-lg sm:text-xl tracking-tight text-brand-bordeaux">
        {BRAND.name}
      </span>
      <span className="text-[9px] sm:text-[10px] uppercase tracking-editorial font-semibold text-brand-gold mt-0.5">
        {BRAND.division}
      </span>
    </a>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const home = pathname === "/";
  // On subpages, anchors must point back to the homepage sections.
  const anchor = (href) => (home ? href : `/${href}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock scroll while the mobile menu is open (body overflow + stop Lenis)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-brand-cream/85 backdrop-blur-xl border-b border-brand-bordeaux/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Logo home={home} />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={anchor(item.href)}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-brand-graphite/75 hover:text-brand-bordeaux transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-brand-gold hover:after:w-full after:transition-all after:duration-300"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:block">
            <CTAButton href={anchor("#lead")} data-testid="nav-cta-diagnosi" className="px-5 py-2.5">
              Richiedi diagnosi
            </CTAButton>
          </div>

          <button
            className="lg:hidden p-2 text-brand-bordeaux"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Apri menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </motion.header>

      {/* Mobile menu — rendered OUTSIDE the header so its fixed positioning
          is relative to the viewport (the header's backdrop-blur would
          otherwise clip fixed descendants to the header box). */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-0 z-[100] bg-brand-bordeaux text-brand-ivory overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-4 h-16 sm:h-20 border-b border-brand-ivory/10">
              <div className="flex flex-col leading-none">
                <span className="font-serif text-lg tracking-tight text-brand-ivory">{BRAND.name}</span>
                <span className="text-[9px] uppercase tracking-editorial font-semibold text-brand-gold mt-0.5">
                  {BRAND.division}
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Chiudi menu"
                className="p-2 text-brand-ivory"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col px-6 py-10 gap-6">
              {NAV.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={anchor(item.href)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-serif text-3xl text-brand-ivory/90 hover:text-brand-gold transition-colors"
                >
                  {item.label}
                </motion.a>
              ))}
              <CTAButton href={anchor("#lead")} variant="gold" onClick={() => setOpen(false)} data-testid="mobile-nav-cta" className="mt-6 self-start">
                Richiedi diagnosi
              </CTAButton>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
